import { DateRange } from '@app/shared/models/date-range';
import { DateUtils, moment } from '@app/utils/date';

export abstract class PortalEvent<T> {
  _original?: T;
  date?: string;
  inPast: boolean;
  title?: string;

  abstract conflictsWith(event: PortalEvent<T>): boolean;
  abstract conflictsWithRange(range: DateRange): boolean;
  abstract equals(event: PortalEvent<T>): boolean;
}

export class MultiDayEvent<T = any> extends PortalEvent<T> {
  end?: string;
  start?: string;

  get endDate() {
    return DateUtils.parse(this.end);
  }

  get range(): DateRange {
    return { from: this.startDate, to: this.endDate };
  }

  get startDate() {
    return DateUtils.parse(this.start);
  }

  constructor(public events: PortalEvent<T>[]) {
    super();

    this.end = events[events.length - 1].date;
    this.start = events[0].date;
    this.title = events[0].title;

    this.inPast = moment(this.end).isBefore(moment().startOf('day'));
  }

  conflictsWith(event): boolean {
    let conflict = false;

    if ('range' in event) {
      conflict = DateUtils.overlaps(this.range, event.range);
    } else if ('jsDate' in event) {
      conflict = DateUtils.inRange(event.jsDate, this.range);
    }

    return conflict;
  }

  conflictsWithRange(range: DateRange): boolean {
    return DateUtils.overlaps(this.range, range);
  }

  equals(event): boolean {
    let equals = false;

    if ('events' in event) {
      if (event.events.length === this.events.length) {
        equals = true;

        for (let i = 0; i < event.events.length; i++) {
          equals = equals && event.events[i]._original.id === event.events[i]._original.id;
        }
      }
    }

    return equals;
  }
}

export class SingleDayEvent<T = any> extends PortalEvent<T> {
  // Make date non-optional
  public date: string;

  get jsDate() {
    return DateUtils.parse(this.date);
  }

  constructor(
    public event: { date: string; _original: T; title: string },
    public excluded: boolean = false,
    public inherited: boolean = false
  ) {
    super();

    this._original = event._original;
    this.date = event.date;
    this.title = event.title;

    this.inPast = moment(this.date).isBefore(moment().startOf('day'));
  }

  conflictsWith(event): boolean {
    let conflict = false;

    if ('range' in event) {
      conflict = DateUtils.inRange(this.jsDate, event.range);
    } else if ('jsDate' in event) {
      conflict = DateUtils.equal(event.jsDate, this.jsDate);
    }

    return conflict;
  }

  conflictsWithRange(range: DateRange): boolean {
    return DateUtils.inRange(this.jsDate, range);
  }

  equals(event): boolean {
    let equals = false;

    if ('_original' in event) {
      equals = event._original.id === (this._original as any).id;
    }

    return equals;
  }
}
